<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';

/** 保存ボタンのUI状態 */
type SupplierForecastUiStatus = 'idle' | 'loading' | 'success' | 'error';

const SUCCESS_DURATION_DEFAULT = 1600;
const ERROR_DURATION_DEFAULT = 2000;

/**
 * 保存ボタンの表示状態を制御するコンポーネント
 * @property saveHandler - 保存処理を実行する非同期関数
 * @property disabled - ボタンを操作不可にするかどうか
 * @property ariaLabel - ボタン用のARIAラベル
 * @property loadingAriaLabel - ローディング表示時のARIAラベル
 * @property successAriaLabel - 成功表示時のARIAラベル
 * @property errorAriaLabel - エラー表示時のARIAラベル
 * @property successDuration - 成功表示からidleへ戻すまでの時間(ms)
 */
const props = defineProps<{
  saveHandler: () => Promise<unknown>;
  disabled?: boolean;
  ariaLabel?: string;
  loadingAriaLabel?: string;
  successAriaLabel?: string;
  errorAriaLabel?: string;
  successDuration?: number;
  canSave?: boolean;
}>();

/**
 * クリックイベントを通知する
 */
const emit = defineEmits<{
  (e: 'status-change', status: SupplierForecastUiStatus): void;
  (e: 'success'): void;
  (e: 'error', error: unknown): void;
}>();

const currentStatus = ref<SupplierForecastUiStatus>('idle');
let successTimer: ReturnType<typeof setTimeout> | undefined;

const isDisabled = computed(() => {
  if (props.disabled) return true;
  if (currentStatus.value === 'loading' || currentStatus.value === 'success') return true;
  if (props.canSave !== undefined && !props.canSave) return true;
  return false;
});

/**
 * UI状態を更新してリスナーへ通知する
 * @param status - 設定する状態
 */
const setStatus = (status: SupplierForecastUiStatus) => {
  currentStatus.value = status;
  emit('status-change', status);
};

/**
 * idle 状態のときのみクリックを許可
 */
const handleClick = () => {
  if (isDisabled.value) return;
  runHandler();
};

/** 成功表示のタイマーを解除する */
const clearSuccessTimer = () => {
  if (successTimer) {
    clearTimeout(successTimer);
    successTimer = undefined;
  }
};

/** 保存処理を実行し、状態遷移を管理する */
const runHandler = async () => {
  if (!props.saveHandler) return;
  clearSuccessTimer();
  setStatus('loading');
  try {
    await props.saveHandler();
    setStatus('success');
    emit('success');
    const duration = props.successDuration ?? SUCCESS_DURATION_DEFAULT;
    successTimer = setTimeout(() => {
      setStatus('idle');
      successTimer = undefined;
    }, duration);
  } catch (error) {
    setStatus('error');
    emit('error', error);
    const duration = props.successDuration ?? ERROR_DURATION_DEFAULT;
    successTimer = setTimeout(() => {
      setStatus('idle');
      successTimer = undefined;
    }, duration);
  }
};

onBeforeUnmount(() => {
  clearSuccessTimer();
});

onMounted(() => {
  emit('status-change', currentStatus.value);
});
</script>

<template>
  <div class="supplier-forecast-save-button">
    <Transition name="supplier-forecast-fade" mode="out-in">
      <button
        v-if="currentStatus === 'idle'"
        key="idle"
        type="button"
        class="supplier-forecast-save-button__icon-button"
        :disabled="isDisabled"
        :aria-label="ariaLabel"
        @click="handleClick"
      >
        <Mdi class="supplier-forecast-save-button__save-icon" icon="save" />
      </button>
      <span
        v-else-if="currentStatus === 'loading'"
        key="loading"
        class="supplier-forecast-save-button__loading"
        role="status"
        aria-live="polite"
        :aria-label="loadingAriaLabel ?? ariaLabel"
      />
      <span
        v-else-if="currentStatus === 'success'"
        key="success"
        class="supplier-forecast-save-button__success"
        role="status"
        aria-live="polite"
        :aria-label="successAriaLabel ?? ariaLabel"
      >
        <Mdi class="supplier-forecast-save-button__success-icon" icon="check_circle" />
      </span>
      <button
        v-else
        key="error"
        type="button"
        class="supplier-forecast-save-button__error"
        :aria-label="errorAriaLabel ?? ariaLabel"
        @click="handleClick"
      >
        <Mdi class="supplier-forecast-save-button__error-icon" icon="error" />
      </button>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.supplier-forecast-save-button {
  position: relative;
  display: inline-block;
  width: 1.75em;
  height: 1.75em;

  & > * {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__icon-button {
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: $semanticPrimary;
    cursor: pointer;
    transition: transform 0.2s ease, opacity 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }

    &:disabled {
      color: $textDisable;
      opacity: 1;
      cursor: default;
      transform: none;
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
  }

  &__save-icon,
  &__success-icon,
  &__error-icon {
    font-size: 1.2rem;
    line-height: 1;
    color: inherit;
  }

  &__loading,
  &__success,
  &__error {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
  }

  &__loading {
    position: relative;
    color: $semanticPrimary;

    &::after {
      content: '';
      width: 0.9em;
      height: 0.9em;
      border-radius: 999px;
      border: 2px solid currentColor;
      border-top-color: transparent;
      animation: supplier-forecast-save-button-spin 0.7s linear infinite;
    }
  }

  &__success {
    color: $semanticSuccess;
    animation: supplier-forecast-save-button-pop 0.4s ease-out;
  }

  &__error {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $semanticError;
    cursor: pointer;
    animation: supplier-forecast-save-button-pop 0.4s ease-out;
    background: transparent;
    border: none;
    padding: 0;
    border-radius: 999px;
    transition: transform 0.2s ease, opacity 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
  }
}

.supplier-forecast-fade-enter-active,
.supplier-forecast-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.supplier-forecast-fade-enter-from,
.supplier-forecast-fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

@keyframes supplier-forecast-save-button-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes supplier-forecast-save-button-pop {
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  60% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
